'use client'

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import { motion, AnimatePresence, Variants } from 'framer-motion';

import { ParagraphSection } from '@/types/drupal';
import type { FoundationClient } from '@/app/api/clients/route';

function nameToSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
}

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] } 
  }
};

const imageReveal: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { duration: 1, ease: "easeOut" } 
  }
};

const testimonialTransition: Variants = {
  enter: { opacity: 0, x: 20, filter: 'blur(5px)' },
  center: { 
    opacity: 1, 
    x: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.5, ease: "easeOut" } 
  },
  exit: { 
    opacity: 0, 
    x: -20, 
    filter: 'blur(5px)',
    transition: { duration: 0.3 } 
  }
};

export default function Testimonials({ data }: { data: ParagraphSection }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    duration: 40,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const totalTestimonials = data.testimonials?.length || 0;
  const [clientMap, setClientMap] = useState<Map<string, FoundationClient>>(new Map());

  useEffect(() => {
    fetch('/api/clients/')
      .then(r => r.ok ? r.json() : [])
      .then((clients: FoundationClient[]) => {
        const map = new Map<string, FoundationClient>();
        clients.forEach(c => map.set(c.slug, c));
        setClientMap(map);
      })
      .catch(() => {});
  }, []);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => onSelect(emblaApi));
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section
      id="testimonials"
      className="Testimonials bg-[#D1E0FA] my-[30px] lg:py-[50px] overflow-hidden relative"
      aria-label="Testimonials"
    >
      <div className="hidden lg:block absolute left-[60px] top-0 z-20 w-px h-[100%] bg-[#0A4492]"></div>
      <div className="hidden lg:block absolute right-[60px] top-0 z-20 w-px h-[100%] bg-[#0A4492]"></div>

      <div className="lg:border-t lg:border-b lg:border-[#0A4492]">
        <div className="section-container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch"
          >
            {/* LEFT SIDE: Content & Hero Image (Span 6) */}
            <div className="lg:col-span-6 border-[#0A4492] lg:border-r pt-[60px] lg:py-[110px] lg:pr-20 flex flex-col justify-between items-center lg:items-start text-center lg:text-left">
              <div className="max-w-[490px] w-full">
                <div className="relative">
                  {/* Eyebrow */}
                  <motion.p variants={fadeInUp} className="lg:absolute left-0 top-[-28px] text-xl lg:text-[34px] text-[#FEFFD9] font-medium mb-2 lg:mb-0">
                    {data.eyebrow}
                  </motion.p>

                  {/* Title */}
                  <motion.div 
                    variants={fadeInUp}
                    className="section-heading text-[#0A4492]"
                    dangerouslySetInnerHTML={{ __html: data.title?.processed || '' }}
                  />

                  {/* Body */}
                  <motion.div 
                    variants={fadeInUp}
                    className="text-[#0A4492] mt-5 lg:mt-[30px]"
                    dangerouslySetInnerHTML={{ __html: data.body?.processed || '' }}
                  />
                </div>

                {data.image?.mediaImage?.url && (
                  <motion.div variants={imageReveal} className="relative w-full mt-[30px] lg:mt-10 px-4 lg:px-0">
                    <Image
                      src={data.image.mediaImage.url}
                      alt={data.image.mediaImage.alt || ''}
                      width={data.image.mediaImage.width || 800}
                      height={data.image.mediaImage.height || 600}
                      className="w-full h-auto object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                  </motion.div>
                )}
              </div>
            </div>

            {/* RIGHT SIDE: Testimonial Slider (Span 6) */}
            <div
              className="relative lg:col-span-6 flex flex-col justify-center py-5 lg:py-[110px] lg:pl-20 items-center lg:items-start"
              role="region"
              aria-roledescription="carousel"
              aria-label="Client testimonials"
            >
              {/* Live region for slide changes */}
              <div aria-live="polite" aria-atomic="true" className="sr-only">
                Testimonial {selectedIndex + 1} of {totalTestimonials}
              </div>

              <div className="overflow-hidden w-full" ref={emblaRef}>
                <div className="flex">
                  {data.testimonials?.map((testimonial, index) => {
                    return (
                      <div
                        key={index}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`Testimonial ${index + 1} of ${totalTestimonials}`}
                        aria-hidden={index !== selectedIndex}
                        className="flex-[0_0_100%] min-w-0 px-6 lg:px-0 lg:pr-4"
                      >
                        <AnimatePresence mode="wait">
                          {index === selectedIndex && (() => {
                            const slug = testimonial.company ? nameToSlug(testimonial.company) : '';
                            const client = clientMap.get(slug);
                            const logoUrl = client?.logo ?? testimonial.companyLogo?.mediaImage?.url ?? null;

                            return (
                              <motion.figure
                                variants={testimonialTransition}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className="max-w-125 mx-auto lg:mx-0 text-center lg:text-left"
                              >
                                <blockquote className="text-[#0A4492] text-sm leading-4 lg:text-[32px] lg:leading-10 italic lg:not-italic font-medium lg:font-normal">
                                  &ldquo;{testimonial.quote?.processed.replace(/<\/?[^>]+(>|$)/g, "")}&rdquo;
                                </blockquote>

                                {/* Company Logo */}
                                <div className="mt-7.5 lg:mt-16.25 h-12 flex items-center justify-center lg:justify-start">
                                  {logoUrl ? (
                                    <div className="bg-white px-4 py-2 rounded shadow-sm border border-gray-100 h-full flex items-center">
                                      {/* eslint-disable-next-line @next/next/no-img-element */}
                                      <img
                                        src={logoUrl}
                                        alt={testimonial.company ?? ''}
                                        className="max-h-8 w-auto object-contain"
                                      />
                                    </div>
                                  ) : testimonial.company ? (
                                    <div className="bg-white px-4 py-2 rounded shadow-sm border border-gray-100">
                                      <span className="font-bold text-[#0A4492] tracking-tighter text-xl uppercase">
                                        {testimonial.company}
                                      </span>
                                    </div>
                                  ) : null}
                                </div>

                                {/* Author Info */}
                                <figcaption className="mt-5 text-sm lg:text-base text-[#404040]">
                                  <span className="font-semibold lg:font-normal">{testimonial.authorName}</span>
                                  <span className="mx-2" aria-hidden="true">/</span>
                                  <cite className="text-[#0A4492] not-italic">{testimonial.authorPosition}</cite>
                                </figcaption>
                              </motion.figure>
                            );
                          })()}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination (Lines) */}
              <div
                className="mt-5 lg:mt-[60px] flex gap-4 justify-center lg:justify-start w-full"
                role="tablist"
                aria-label="Testimonial navigation"
              >
                {data.testimonials?.map((_, i) => (
                  <button
                    key={i}
                    role="tab"
                    onClick={() => emblaApi?.scrollTo(i)}
                    aria-selected={i === selectedIndex}
                    aria-label={`Go to testimonial ${i + 1}`}
                    className={`
                      h-1 lg:w-[35px] transition-all duration-500 rounded-full cursor-pointer
                      ${i === selectedIndex ? 'w-12 bg-[#0A4492]' : 'w-6 bg-[#0A4492]/30'}
                    `}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
